import { settlementRows } from "services/FinanceService";
import { Link } from "react-router-dom";
import {
  DataTable,
  LoadState,
  Panel,
  Stats,
  localDate,
  money,
  useFinanceData,
} from "./shared";

export default function OperationsSummary() {
  const today = localDate();
  const income = useFinanceData("/reports/income-statement", {
    startDate: `${today.slice(0, 7)}-01`,
    endDate: today,
  });
  const settlements = useFinanceData("/settlements", {
    page: 0,
    size: 5,
    status: "PENDING",
  });
  const accounts = useFinanceData("/coa");
  const coa = accounts.data || [];
  return (
    <>
      <Panel
        title="Month-to-date performance"
        subtitle="Revenue and provider expenses for the current reporting month."
      >
        <div className="toolbar" style={{ marginBottom: 16 }}>
          <Link className="table-link" to="/finance-reports">
            Open income statement →
          </Link>
          <button className="fin-button secondary" onClick={income.refresh}>
            Refresh income
          </button>
        </div>
        <LoadState {...income} />
        {income.data && (
          <>
            <p className="report-period">
              {income.data.startDate} — {income.data.endDate}
            </p>
            <Stats
              items={[
                ["Gross MDR revenue", money(income.data.grossMdrRevenue)],
                [
                  "Provider expenses",
                  money(income.data.totalProviderInterchangeExpense),
                ],
                ["Net platform margin", money(income.data.netPlatformMargin)],
              ]}
            />
          </>
        )}
      </Panel>
      <div className="finance-grid">
        <Panel
          title="Pending settlement batches"
          subtitle="The first five pending batches; open settlements for the complete queue."
        >
          <div className="toolbar" style={{ marginBottom: 16 }}>
            <Link className="table-link" to="/settlements">
              Review settlements →
            </Link>
            <button
              className="fin-button secondary"
              onClick={settlements.refresh}
            >
              Refresh batches
            </button>
          </div>
          <LoadState {...settlements} />
          {settlements.data && (
            <>
              <p className="report-period">
                {settlements.data.totalElements ?? "—"} pending batches
              </p>
              <DataTable
                rowKey="batchReference"
                rows={settlementRows(settlements.data)}
                columns={[
                  { key: "batchReference", label: "Batch" },
                  { key: "merchantName", label: "Merchant" },
                  {
                    key: "batchTotal",
                    label: "Amount",
                    render: (row) => money(row.batchTotal),
                  },
                  { key: "transactionCount", label: "Transactions" },
                ]}
              />
            </>
          )}
        </Panel>
        <Panel
          title="Chart of accounts status"
          subtitle="Account configuration counts, independent of ledger balance health."
        >
          <div className="toolbar" style={{ marginBottom: 16 }}>
            <Link className="table-link" to="/chart-of-accounts">
              Manage accounts →
            </Link>
            <button className="fin-button secondary" onClick={accounts.refresh}>
              Refresh accounts
            </button>
          </div>
          <LoadState {...accounts} />
          {accounts.data && (
            <>
              <p className="report-period">
                {coa.length} accounts ·{" "}
                {coa.filter((a) => a.active === true).length} active ·{" "}
                {coa.filter((a) => a.active === false).length} inactive
              </p>
              <DataTable
                rowKey="type"
                rows={[
                  "ASSET",
                  "LIABILITY",
                  "EQUITY",
                  "REVENUE",
                  "EXPENSE",
                ].map((type) => ({
                  type,
                  total: coa.filter((a) => a.type === type).length,
                  active: coa.filter(
                    (a) => a.type === type && a.active === true
                  ).length,
                }))}
                columns={[
                  { key: "type", label: "Classification" },
                  { key: "total", label: "Total" },
                  { key: "active", label: "Active" },
                ]}
              />
            </>
          )}
        </Panel>
      </div>
    </>
  );
}
